
import React from 'react';
import { Link, useNavigate } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface NursingHomeListProps {
  nursingHomes: any[];
  countySlug?: string;
  className?: string;
  selectedHome: string | null;
  setSelectedHome: (nhSlug: string | null) => void;
  defaultExpanded?: boolean;
  navigateOnSelect?: boolean;
}

export function NursingHomesList({ 
  nursingHomes = [], 
  countySlug, 
  className = '', 
  selectedHome, 
  setSelectedHome,
  defaultExpanded = true,
  navigateOnSelect = false
}: NursingHomeListProps) {
  const [collapsed, setCollapsed] = React.useState(!defaultExpanded);
  const navigate = useNavigate();

  if (!nursingHomes || nursingHomes.length === 0) {
    return null;
  }

  if (collapsed) {
    return (
      <div className="absolute top-4 right-4 z-10">
        <Button 
          variant="default" 
          size="sm" 
          onClick={() => setCollapsed(false)}
          className="bg-white text-grandpal-green hover:bg-grandpal-green hover:text-white shadow-md"
        >
          <MapPin className="h-4 w-4 mr-1" />
          Show {nursingHomes.length} locations
        </Button>
      </div>
    );
  }

  const handleHomeClick = (e: React.MouseEvent, home: any) => {
    e.preventDefault();
    setSelectedHome(home.nh_slug);
    
    if (navigateOnSelect) {
      setTimeout(() => {
        navigate(`/nursing-homes/${home.county_slug}/${home.nh_slug}`);
      }, 300); // Small delay to show the selection
    }
  };

  return (
    <div className={`absolute top-4 right-4 z-10 bg-white rounded-md shadow-md overflow-hidden w-[350px] max-h-[60%] flex flex-col ${className}`}>
      <div className="flex justify-between items-center p-4 pb-2 border-b">
        <div>
          <h3 className="font-medium">Nursing Homes ({nursingHomes.length})</h3>
          <p className="text-xs text-muted-foreground">Click on a nursing home to view location</p>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0"
          onClick={() => setCollapsed(true)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="overflow-y-auto flex-1 p-4">
        <div className="space-y-3">
          {nursingHomes.map((home) => (
            <Link 
              key={home.nh_slug}
              to={`/nursing-homes/${home.county_slug || countySlug}/${home.nh_slug}`}
              className={`flex items-start p-3 rounded-md transition-colors ${
                selectedHome === home.nh_slug 
                  ? 'bg-grandpal-green/10 border border-grandpal-green/30' 
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
              onClick={(e) => handleHomeClick(e, home)}
            >
              <MapPin className={`h-5 w-5 mt-0.5 mr-2 flex-shrink-0 ${
                selectedHome === home.nh_slug ? 'text-grandpal-green' : 'text-gray-400'
              }`} />
              <div>
                <span className={`font-medium ${selectedHome === home.nh_slug ? 'text-grandpal-green' : ''}`}>
                  {home.center_name}
                </span>
                
                <p className="text-xs text-gray-500 line-clamp-1 mb-1">
                  {home.standardized_address || home.street_address}
                </p>
                
                <div className="flex flex-wrap gap-1 mt-1">
                  {home.category && (
                    <Badge variant="outline" className={`text-xs py-0 px-1 ${
                      home.category.toLowerCase() === 'public' 
                        ? 'bg-blue-50 text-blue-700 border-blue-200' 
                        : home.category.toLowerCase() === 'private' 
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : ''
                    }`}>
                      {home.category}
                    </Badge>
                  )}
                  {home.max_residents && (
                    <Badge variant="outline" className="text-xs py-0 px-1">
                      {home.max_residents} residents
                    </Badge>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-auto px-4 py-3 border-t">
        <Link
          to={`/nursing-homes/${countySlug}`}
          className="text-sm text-grandpal-green hover:underline flex items-center justify-center"
        >
          {countySlug ? 'View all nursing homes in this county' : 'View nursing homes by county'}
        </Link>
      </div>
    </div>
  );
}
